/* eslint-disable canvas/no-swallow-errors-in-catch */
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { DatabaseTableSkeleton } from '@databases/frontend/DatabaseContentSkeleton';
import { useMarkMountTimings } from '@databases/frontend-metrics/useMarkMountTimings';
import { logError } from '@databases/logs';
import React from 'react';
import { createRoot } from 'react-dom/client';

import { getRootSkeletonAppElement } from './elements';

/**
 * Mounts the app skeleton early in the bootstrapping process.
 * This shows the user a loading component (the Skeleton) much
 * sooner than waiting till later in the lifecycle.
 *
 * This returns an unmount function which is to be called just
 * after mounting the rest of the application.
 */
export function mountSkeleton() {
  const skeletonEl = getRootSkeletonAppElement();

  if (skeletonEl) {
    // place this above #app so it can render
    skeletonEl.style.zIndex = '2';

    const skeletonRoot = createRoot(skeletonEl);

    try {
      skeletonRoot.render(<InitialDatabaseSkeleton />);
    } catch (e) {
      if (e instanceof Error) {
        logError(e);
      }

      skeletonEl.style.zIndex = '0';
    }

    return () => {
      skeletonRoot.unmount();

      // put this behind the #app
      skeletonEl.style.zIndex = '0';
    };
  }

  return () => null;
}

function InitialDatabaseSkeleton() {
  useMarkMountTimings({
    onStart: () => GlobalPageLoadExperience.mark('mount-bootstrap-skeleton-start'),
    onMounted: () => GlobalPageLoadExperience.mark('mount-bootstrap-skeleton-end'),
  });

  return <DatabaseTableSkeleton />;
}
